import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faPlayCircle, faLock } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { formatDuration } from '@/utils/helpers';
import { useUser } from '@/contexts/UserContext';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import type { Video } from '@/models';

interface VideoReferenceCardsProps {
  videos: Video[];
}

// Rendert die vom LLM (video_reference-Tool) an eine Antwort angehängten Videos als anklickbare Karten
export default function VideoReferenceCards({ videos }: VideoReferenceCardsProps) {
  const { isStaff } = useUser();
  const { hasCourseAccess, hasActiveSubscription } = useFeatureAccess();

  // Das LLM kann ein Video versehentlich mehrfach referenzieren
  const uniqueVideos = videos.filter((video, index) => videos.findIndex(v => v.id === video.id) === index);

  if (uniqueVideos.length === 0) {
    return null;
  }

  // Das Backend referenziert nur Videos, auf die der Nutzer Zugriff hat. Wird der Zugriff aber
  // nachträglich entzogen (Abo abgelaufen, Freischaltung zurückgenommen), bleiben alte Nachrichten
  // bestehen - dann soll die Karte auf die Kurs-gesperrt-Seite zeigen statt ins Leere zu laufen.
  //
  // Video kennt weder das `free`-Flag noch die Community-Flags des Kurses, deshalb sperren wir nur,
  // wenn die Lage eindeutig ist: mit aktivem Abo braucht JEDER Kurs eine explizite Freischaltung
  // (freie Kurse sind nur OHNE Abo frei, siehe FeatureConfig.IsCourseAllowed im Backend).
  // Alle übrigen Fälle fängt VideoDetailView über den API-Fehler ab.
  const isCourseLocked = (video: Video): boolean =>
    !isStaff && hasActiveSubscription() && !hasCourseAccess(video.courseID);

  return (
    <div className="mt-2">
      <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
        Passende Videos
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {uniqueVideos.map((video) => {
          const locked = isCourseLocked(video);

          return (
            <Link
              key={video.id}
              to={locked
                ? `/feature-locked/course/${video.courseID}`
                : `/courses/${video.courseID}/modules/${video.moduleID}/videos/${video.id}`}
              title={locked ? 'Dieser Kurs ist nicht freigeschaltet' : undefined}
              className="group bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all hover:shadow-lg flex flex-col cursor-pointer"
            >
              {/* Thumbnail */}
              <div className="relative w-full aspect-video bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
                {video.thumbnail !== null ? (
                  <img
                    src={APIRoutes.getBlobContentUrl(video.thumbnail)}
                    alt={video.title}
                    className={`w-full h-full object-cover ${locked ? 'grayscale' : ''}`}
                    loading="lazy"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faVideo}
                    className="w-8 h-8 text-gray-400 dark:text-gray-500"
                  />
                )}
                {locked ? (
                  /* Gesperrt-Overlay: dauerhaft sichtbar, damit der Klick nicht überrascht */
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-1">
                    <FontAwesomeIcon icon={faLock} className="w-6 h-6 text-white" />
                    <span className="text-[10px] font-medium uppercase tracking-wider text-white">
                      Gesperrt
                    </span>
                  </div>
                ) : (
                  /* Play overlay */
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faPlayCircle}
                      className="w-10 h-10 text-white"
                    />
                  </div>
                )}
                {video.duration !== null && (
                  <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                    {formatDuration(video.duration)}
                  </span>
                )}
              </div>

              {/* Video Info */}
              <div className="p-2 flex-1 flex flex-col gap-1">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">
                  {video.title}
                </h4>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-auto">
                  {locked ? 'Kurs nicht freigeschaltet' : `Video ${video.globalIndex}`}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
